import { assertEnv } from "../env";

const CLIENT_ORIGIN_HEADER = "x-client-origin";

/**
 * Builds the `x-client-origin` wire value from `NEXT_PUBLIC_CLIENT_ORIGIN_APP`
 * and `NEXT_PUBLIC_CLIENT_ORIGIN_ENV`. Next.js inlines `NEXT_PUBLIC_*` for the
 * client but these variables are also readable on the server, so we use one
 * pair everywhere instead of duplicating non-public names.
 *
 * The env segment (e.g. `dev`, `prod`) is intentionally short for the header.
 * It comes only from `NEXT_PUBLIC_CLIENT_ORIGIN_ENV` and is independent of
 * `NEXT_PUBLIC_DEPLOY_ENV`, which may use different spellings (e.g. `production`).
 */
function buildClientOriginHeaderValue(): string {
  const app = assertEnv(
    "NEXT_PUBLIC_CLIENT_ORIGIN_APP",
    process.env.NEXT_PUBLIC_CLIENT_ORIGIN_APP,
  );
  const env = assertEnv(
    "NEXT_PUBLIC_CLIENT_ORIGIN_ENV",
    process.env.NEXT_PUBLIC_CLIENT_ORIGIN_ENV,
  );
  return `${app}-${env}`;
}

/**
 * Returns a header object suitable for spreading into a fetch `headers` field.
 *
 * Throws if either required `NEXT_PUBLIC_CLIENT_ORIGIN_*` variable is missing.
 */
function getClientOriginHeader(): Record<string, string> {
  return { [CLIENT_ORIGIN_HEADER]: buildClientOriginHeaderValue() };
}

/**
 * Normalizes the input headers to a `Headers` instance and sets
 * `x-client-origin`, overwriting any caller-supplied value.
 */
function applyClientOriginHeader(headers: HeadersInit | undefined): Headers {
  const merged = new Headers(headers);
  merged.set(CLIENT_ORIGIN_HEADER, buildClientOriginHeaderValue());
  return merged;
}

/**
 * `fetch` wrapper that ensures `x-client-origin` is set on every request.
 * Use this for runtime callsites that don't go through `apiFetch` /
 * `ApiClient`.
 *
 * Header merge order (later sources win except `x-client-origin`):
 *   1. Headers from a `Request` input (when `input` is a `Request`)
 *   2. `init.headers`
 *   3. `x-client-origin` (always wins)
 *
 * This preserves the native `fetch` contract: callers can pass a fully formed
 * `Request` and the wrapper will not silently drop its headers.
 */
function fetchWithClientOrigin(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const seed = input instanceof Request ? input.headers : undefined;
  const merged = new Headers(seed);

  if (init.headers !== undefined) {
    new Headers(init.headers).forEach((value, key) => {
      merged.set(key, value);
    });
  }

  merged.set(CLIENT_ORIGIN_HEADER, buildClientOriginHeaderValue());

  return globalThis.fetch(input, {
    ...init,
    headers: merged,
  });
}

export {
  applyClientOriginHeader,
  CLIENT_ORIGIN_HEADER,
  fetchWithClientOrigin,
  getClientOriginHeader,
};
